import { useState, useCallback } from 'react';
import { Search, Filter, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SearchFilters } from '@/features/search/hooks/useSearch';

interface PaperSearchProps {
  onSearch: (filters: SearchFilters) => void;
  isLoading?: boolean;
}

const FIELDS_OF_STUDY = [
  'Computer Science',
  'Medicine',
  'Biology',
  'Chemistry',
  'Physics',
  'Psychology',
  'Mathematics',
  'Engineering',
  'Environmental Science',
  'Economics',
  'Sociology',
  'Political Science',
  'Philosophy',
  'Linguistics',
  'Education',
  'Business',
  'History',
  'Geography',
  'Art',
  'Materials Science',
];

const YEAR_OPTIONS = [
  { label: 'Any year', value: '' },
  { label: '2024-2025', value: '2024-2025' },
  { label: '2020-2025', value: '2020-2025' },
  { label: '2015-2025', value: '2015-2025' },
  { label: '2010-2025', value: '2010-2025' },
  { label: '2000-2025', value: '2000-2025' },
];

export function PaperSearch({ onSearch, isLoading }: PaperSearchProps) {
  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [year, setYear] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [openAccessOnly, setOpenAccessOnly] = useState(false);

  const handleSearch = useCallback(() => {
    if (!query.trim()) return;
    onSearch({
      query: query.trim(),
      year: year || undefined,
      fieldsOfStudy: selectedFields.length > 0 ? selectedFields : undefined,
      openAccessOnly,
    });
  }, [query, year, selectedFields, openAccessOnly, onSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const toggleField = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const clearFilters = () => {
    setYear('');
    setSelectedFields([]);
    setOpenAccessOnly(false);
  };

  const hasFilters = year || selectedFields.length > 0 || openAccessOnly;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search papers on Semantic Scholar..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-10"
          />
        </div>
        <Button onClick={handleSearch} disabled={isLoading || !query.trim()}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            'Search'
          )}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowFilters(!showFilters)}
          className={hasFilters ? 'border-indigo-300 bg-indigo-50' : ''}
        >
          <Filter className="h-4 w-4" />
        </Button>
      </div>

      {showFilters && (
        <div className="p-4 rounded-lg border bg-slate-50 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-slate-700">Filters</h4>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Publication year</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Any year" />
                </SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value || 'any'}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="open-access"
                checked={openAccessOnly}
                onCheckedChange={setOpenAccessOnly}
              />
              <Label htmlFor="open-access" className="text-sm">
                Open access only
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Fields of study</Label>
            <div className="flex flex-wrap gap-1.5">
              {FIELDS_OF_STUDY.map((field) => (
                <Badge
                  key={field}
                  variant={selectedFields.includes(field) ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => toggleField(field)}
                >
                  {field}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
